import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatINR, imgUrl } from "@/lib/api";
import { useCart } from "@/contexts/CartContext";
import { useWishlist } from "@/contexts/WishlistContext";
import { Minus, Plus, Heart, Truck, Shield, Sparkles, Star } from "lucide-react";
import { toast } from "sonner";
import { ProductCard } from "@/components/ProductCard";

export default function ProductDetail() {
  const { id } = useParams();
  const { add } = useCart();
  const { has, toggle } = useWishlist();
  const [p, setP] = useState(null);
  const [related, setRelated] = useState([]);
  const [qty, setQty] = useState(1);
  const [activeImg, setActiveImg] = useState(0);
  const [selectedVariantId, setSelectedVariantId] = useState(null);

  useEffect(() => {
    setActiveImg(0);
    setQty(1);
    setSelectedVariantId(null);
    api.get(`/products/${id}`).then((r) => {
      setP(r.data);
      // Default to first in-stock variant if variants exist
      const inStock = (r.data.variants || []).find((v) => (v.stock || 0) > 0);
      const firstVar = inStock || (r.data.variants || [])[0];
      if (firstVar) setSelectedVariantId(firstVar.variant_id);
      api.get(`/products?category=${r.data.category}&limit=8`).then((rr) =>
        setRelated(rr.data.filter((x) => x.product_id !== r.data.product_id).slice(0, 4))
      );
    }).catch(() => toast.error("Product not found"));
  }, [id]);

  const variant = useMemo(() => {
    if (!p || !selectedVariantId) return null;
    return (p.variants || []).find((v) => v.variant_id === selectedVariantId) || null;
  }, [p, selectedVariantId]);

  if (!p) return <div className="max-w-[1400px] mx-auto px-6 py-24 text-center text-[#7c7273]">Loading…</div>;

  // Effective info: variant overrides when present
  const effImages = (variant?.images?.length ? variant.images : p.images) || [];
  const effPrice = variant?.price ?? p.price;
  const effStock = variant ? (variant.stock || 0) : (p.stock || 0);
  const discount = p.compare_at_price ? Math.round(((p.compare_at_price - effPrice) / p.compare_at_price) * 100) : 0;
  const outOfStock = effStock <= 0;

  const onAdd = () => {
    if (outOfStock) return toast.error("This colour is currently out of stock");
    if ((p.variants || []).length > 0 && !variant) return toast.error("Please choose a colour");
    add(p, qty, variant);
  };

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10" data-testid="product-detail-page">
      {/* breadcrumb */}
      <nav className="text-xs tracking-[0.15em] uppercase text-[#7c7273] mb-6">
        <Link to="/" className="hover:text-[#7a1e2b]">Home</Link> / <Link to="/shop" className="hover:text-[#7a1e2b]">Shop</Link> / <Link to={`/shop?category=${p.category}`} className="hover:text-[#7a1e2b]">{p.category}</Link>
      </nav>

      <div className="grid lg:grid-cols-2 gap-10 lg:gap-16">
        {/* Gallery */}
        <div>
          <div className="bg-[#f5f3ef] aspect-[4/5] overflow-hidden mb-4">
            {activeImg === -1 && p.video_url ? (
              <video src={imgUrl(p.video_url)} controls autoPlay loop className="w-full h-full object-cover bg-black" data-testid="product-video" />
            ) : (
              <img src={imgUrl(effImages[activeImg] || effImages[0])} alt={p.name} className="w-full h-full object-cover" data-testid="product-main-image" />
            )}
          </div>
          {(effImages.length > 1 || p.video_url) && (
            <div className="flex gap-3 flex-wrap">
              {effImages.map((img, i) => (
                <button
                  key={img + i}
                  onClick={() => setActiveImg(i)}
                  className={`w-20 h-24 overflow-hidden border ${activeImg === i ? "border-[#7a1e2b]" : "border-[#e8e4db]"}`}
                  data-testid={`thumb-${i}`}
                >
                  <img src={imgUrl(img)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
              {p.video_url && (
                <button
                  onClick={() => setActiveImg(-1)}
                  className={`w-20 h-24 overflow-hidden border relative bg-black flex items-center justify-center ${activeImg === -1 ? "border-[#7a1e2b]" : "border-[#e8e4db]"}`}
                  data-testid="thumb-video"
                >
                  <span className="text-white text-2xl">▶</span>
                  <span className="absolute bottom-0.5 left-0.5 text-white text-[8px] tracking-widest uppercase">Video</span>
                </button>
              )}
            </div>
          )}
        </div>

        {/* Info */}
        <div className="lg:sticky lg:top-28 lg:self-start">
          <div className="text-[11px] tracking-[0.3em] uppercase text-[#c9a34a] mb-3">{p.category} · {p.fabric}</div>
          <h1 className="font-serif text-3xl sm:text-4xl text-[#2b1b1c] leading-tight">{p.name}</h1>

          <div className="flex items-center gap-2 mt-3 text-sm text-[#7c7273]">
            <Star size={14} className="text-[#c9a34a] fill-[#c9a34a]" />
            <span>{p.rating} · {p.review_count} reviews</span>
          </div>

          <div className="flex items-baseline gap-3 mt-6">
            <span className="font-serif text-3xl text-[#7a1e2b]" data-testid="pdp-price">{formatINR(effPrice)}</span>
            {p.compare_at_price && p.compare_at_price > effPrice && (
              <>
                <span className="text-[#7c7273] line-through">{formatINR(p.compare_at_price)}</span>
                <span className="text-sm text-[#4a7a59]">Save {discount}%</span>
              </>
            )}
          </div>
          <div className="text-xs text-[#7c7273] mt-1">Inclusive of all taxes</div>

          <div className="h-px w-full bg-[#e8e4db] my-8" />

          <p className="text-[#2b1b1c] leading-relaxed">{p.description}</p>

          {/* Colour variants */}
          {(p.variants || []).length > 0 && (
            <div className="mt-8" data-testid="pdp-variants">
              <div className="text-[11px] tracking-[0.3em] uppercase text-[#7a1e2b] mb-3">
                Colour · <span className="text-[#2b1b1c] normal-case tracking-normal">{variant?.color || "Choose a colour"}</span>
              </div>
              <div className="flex flex-wrap gap-3">
                {p.variants.map((v) => {
                  const isSelected = v.variant_id === selectedVariantId;
                  const isOOS = (v.stock || 0) <= 0;
                  return (
                    <button
                      key={v.variant_id}
                      onClick={() => { setSelectedVariantId(v.variant_id); setActiveImg(0); }}
                      className={`group flex flex-col items-center gap-1 focus:outline-none`}
                      title={`${v.color}${isOOS ? " — sold out" : ""}`}
                      data-testid={`variant-swatch-${v.color}`}
                      aria-pressed={isSelected}
                    >
                      <span
                        className={`w-10 h-10 rounded-full border-2 transition-all relative ${isSelected ? "border-[#7a1e2b] ring-2 ring-[#c9a34a] ring-offset-2" : "border-[#e8e4db] group-hover:border-[#7a1e2b]"} ${isOOS ? "opacity-40" : ""}`}
                        style={{ backgroundColor: v.color_hex || "#c9a34a" }}
                      >
                        {isOOS && (
                          <span className="absolute inset-0 flex items-center justify-center text-white text-lg font-thin">×</span>
                        )}
                      </span>
                      <span className={`text-[10px] tracking-widest uppercase ${isSelected ? "text-[#7a1e2b]" : "text-[#7c7273]"}`}>{v.color}</span>
                    </button>
                  );
                })}
              </div>
              {outOfStock && (
                <div className="mt-3 text-xs text-[#b33939]" data-testid="pdp-out-of-stock">This colour is sold out. Please pick another.</div>
              )}
              {!outOfStock && effStock <= 3 && (
                <div className="mt-3 text-xs text-[#b58600]" data-testid="pdp-low-stock">Only {effStock} left in this colour</div>
              )}
            </div>
          )}

          {/* Running blouse note (replaces the old blouse size selector) */}
          <div className="mt-6 bg-[#f5f3ef] px-4 py-3 text-sm text-[#2b1b1c] border-l-2 border-[#c9a34a]" data-testid="blouse-note">
            <strong className="font-serif">Running blouse piece included.</strong> Ready for your tailor to stitch to your measurements.
          </div>

          {/* Qty & Add */}
          <div className="mt-8 flex gap-4 items-center">
            <div className="flex items-center border border-[#e8e4db]">
              <button onClick={() => setQty(Math.max(1, qty - 1))} className="p-3 hover:bg-[#f5f3ef]" data-testid="pdp-qty-minus"><Minus size={14} /></button>
              <span className="px-5 min-w-[40px] text-center">{qty}</span>
              <button onClick={() => setQty(qty + 1)} className="p-3 hover:bg-[#f5f3ef]" data-testid="pdp-qty-plus"><Plus size={14} /></button>
            </div>
            <button
              className="btn-primary flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={onAdd}
              disabled={outOfStock}
              data-testid="pdp-add-to-cart"
            >
              {outOfStock ? "Sold out" : "Add to Bag"}
            </button>
            <button
              onClick={() => toggle(p.product_id, p.name)}
              className={`border p-3 transition-colors ${has(p.product_id) ? "border-[#7a1e2b] bg-[#7a1e2b] text-[#fcfbf8]" : "border-[#e8e4db] hover:border-[#7a1e2b] hover:text-[#7a1e2b]"}`}
              aria-label={has(p.product_id) ? "Remove from wishlist" : "Add to wishlist"}
              data-testid="pdp-wishlist"
            >
              <Heart size={18} className={has(p.product_id) ? "fill-current" : ""} />
            </button>
          </div>

          {/* Trust icons */}
          <div className="mt-10 grid grid-cols-3 gap-4 text-xs text-[#7c7273]">
            <div className="flex flex-col items-center gap-2 text-center">
              <Truck size={20} className="text-[#c9a34a]" />
              <span>Free shipping above ₹2,000</span>
            </div>
            <div className="flex flex-col items-center gap-2 text-center">
              <Shield size={20} className="text-[#c9a34a]" />
              <span>Authentic weaves</span>
            </div>
            <div className="flex flex-col items-center gap-2 text-center">
              <Sparkles size={20} className="text-[#c9a34a]" />
              <span>Signature packaging</span>
            </div>
          </div>
        </div>
      </div>

      {/* Related */}
      {related.length > 0 && (
        <section className="mt-24">
          <h2 className="font-serif text-3xl text-[#2b1b1c] mb-8">You may also love</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-12">
            {related.map((r, i) => <ProductCard key={r.product_id} product={r} index={i} />)}
          </div>
        </section>
      )}
    </div>
  );
}
